import React from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Slide, Stack, Typography, alpha, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Close as CloseIcon, Bookmark as ShortlistIcon, Delete as RemoveIcon,
} from '@mui/icons-material';
import type { TransitionProps } from '@mui/material/transitions';
import type { WineListRenderItem } from '../../types/venue';

/**
 * W10 — guest "My Shortlist" dialog.
 *
 * Renders the wines the guest has hearted on the public list. Tap any row
 * to open the full WineDetail modal (parent owns the routing). The remove
 * icon strips a single wine from the shortlist; "Clear all" wipes it.
 *
 * Frontend-only. State lives in localStorage via `useShortlist` in the
 * parent — this component is a pure render + callback surface.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  /** All shortlisted items in the order the guest added them. */
  items: WineListRenderItem[];
  onRemove: (itemId: string) => void;
  onClearAll: () => void;
  onSelectItem: (item: WineListRenderItem) => void;
}

const SlideUp = React.forwardRef(function SlideUp(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const SERIF = '"Cormorant Garamond", "EB Garamond", Georgia, "Times New Roman", serif';

const formatPrice = (amount: number | null | undefined, currency: string | null | undefined): string => {
  if (amount == null) return '';
  return `${currency ? currency + ' ' : ''}${amount.toFixed(0)}`;
};

const ShortlistDialog: React.FC<Props> = ({ open, onClose, items, onRemove, onClearAll, onSelectItem }) => {
  const theme = useTheme();
  // Match the WineDetail modal's behaviour: bottom-sheet on phones,
  // centered dialog on tablet+.
  const isTablet = useMediaQuery('(min-width: 900px)');
  const fullScreen = !isTablet;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      TransitionComponent={fullScreen ? SlideUp : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'background.paper',
          backgroundImage: 'none',
          color: 'text.primary',
          borderRadius: fullScreen ? '20px 20px 0 0' : 3,
          ...(fullScreen ? { mt: 4 } : {}),
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          py: 1.5,
          px: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <ShortlistIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          <Typography
            sx={{
              fontFamily: SERIF,
              fontSize: { xs: '1.4rem', md: '1.6rem' },
              fontWeight: 500,
              color: 'text.primary',
              lineHeight: 1,
            }}
          >
            My Shortlist
          </Typography>
          {items.length > 0 && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', ml: 0.5, fontWeight: 600 }}
            >
              · {items.length}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, borderColor: alpha(theme.palette.divider, 0.5) }}>
        {items.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
            <Typography sx={{ fontStyle: 'italic', fontFamily: SERIF, fontSize: '1.1rem' }}>
              No wines yet.
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.disabled' }}>
              Tap the heart on any wine to add it here.
            </Typography>
          </Box>
        ) : (
          <Stack divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
            {items.map(item => (
              <Box
                key={item.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 2,
                  cursor: 'pointer',
                  transition: 'background-color .15s ease',
                  '&:hover, &:active': { backgroundColor: alpha('#fff', 0.02) },
                }}
                onClick={() => onSelectItem(item)}
                role="button"
                tabIndex={0}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {item.producer && (
                    <Typography
                      sx={{
                        fontSize: '0.68rem',
                        letterSpacing: '0.16em',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        fontWeight: 500,
                        mb: 0.25,
                      }}
                      noWrap
                    >
                      {item.producer}
                    </Typography>
                  )}
                  <Typography
                    sx={{
                      fontFamily: SERIF,
                      fontSize: '1.1rem',
                      fontWeight: 500,
                      color: 'text.primary',
                      lineHeight: 1.2,
                    }}
                  >
                    {item.name ?? 'Unknown wine'}
                    {item.vintage && (
                      <Box
                        component="span"
                        sx={{ ml: 0.65, color: 'text.secondary', fontStyle: 'italic', fontSize: '0.85em' }}
                      >
                        {item.vintage}
                      </Box>
                    )}
                  </Typography>
                </Box>
                {(item.bottlePrice != null || item.price != null) && (
                  <Typography
                    sx={{
                      fontFamily: SERIF,
                      fontSize: '1.05rem',
                      fontWeight: 600,
                      color: 'text.primary',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    {formatPrice(item.bottlePrice ?? item.price, item.currency)}
                  </Typography>
                )}
                <IconButton
                  size="small"
                  onClick={e => { e.stopPropagation(); onRemove(item.id); }}
                  aria-label="Remove from shortlist"
                  sx={{ color: 'text.disabled', flexShrink: 0, '&:hover': { color: 'text.primary' } }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.75, justifyContent: 'space-between' }}>
        <Button
          onClick={onClearAll}
          disabled={items.length === 0}
          sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 500 }}
        >
          Clear all
        </Button>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShortlistDialog;
